/**
 * Weekly + Daily visual calendar grid.
 *
 * Pure CSS-grid based. Rows = time slots, columns = days (week view) or
 * staff members (day view). Cards are positioned absolutely inside each
 * column based on start/end times.
 *
 * Status → colour map matches the table view so the language is consistent
 * across the page.
 */
import { useMemo } from 'react';
import { Card } from '@/components/ui/card';

const STATUS_STYLE = {
  pending_approval: { bg: 'bg-blue-100', border: 'border-l-blue-500', text: 'text-blue-900' },
  scheduled:        { bg: 'bg-emerald-100', border: 'border-l-emerald-500', text: 'text-emerald-900' },
  completed:        { bg: 'bg-gray-100', border: 'border-l-gray-400', text: 'text-gray-700' },
  cancelled:        { bg: 'bg-red-50', border: 'border-l-red-400', text: 'text-red-800' },
  rejected:         { bg: 'bg-red-50', border: 'border-l-red-400', text: 'text-red-800' },
  no_show:          { bg: 'bg-amber-100', border: 'border-l-amber-500', text: 'text-amber-900' },
};

const toMinutes = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

const fmtTime = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

/** Build the list of columns for the calendar.
 *  Week: 7 days. Day: list of staff (+ "Tümü" pseudo column when no staff filter).
 */
function buildColumns({ view, anchorDate, staff }) {
  if (view === 'week') {
    const d = new Date(anchorDate + 'T00:00:00');
    // Monday of the same week (JS day 0=Sun..6=Sat, ISO: Mon=0)
    const js = d.getDay();
    const offset = js === 0 ? -6 : 1 - js;
    const monday = new Date(d);
    monday.setDate(d.getDate() + offset);
    return Array.from({ length: 7 }).map((_, i) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      const dateStr = day.toISOString().split('T')[0];
      return {
        key: dateStr,
        title: DAYS_TR[i],
        subtitle: `${String(day.getDate()).padStart(2, '0')}.${String(day.getMonth() + 1).padStart(2, '0')}`,
        match: (apt) => apt.date === dateStr,
        isToday: dateStr === new Date().toISOString().split('T')[0],
      };
    });
  }
  // day view
  if (!staff || staff.length === 0) {
    return [{
      key: 'all',
      title: 'Randevular',
      subtitle: anchorDate,
      match: () => true,
      isToday: anchorDate === new Date().toISOString().split('T')[0],
    }];
  }
  return [
    { key: '__no_staff__', title: 'Personelsiz', subtitle: anchorDate, match: (a) => !a.staff_id },
    ...staff.map((s) => ({
      key: s.id, title: s.name, subtitle: anchorDate, match: (a) => a.staff_id === s.id,
    })),
  ];
}

export const AppointmentCalendar = ({
  view = 'week',
  anchorDate,
  appointments,
  staff = [],
  hourStart = 8,
  hourEnd = 21,
  onCardClick,
}) => {
  const slotPx = 32; // 30-min row height
  const columns = useMemo(
    () => buildColumns({ view, anchorDate, staff }),
    [view, anchorDate, staff],
  );

  // Filter appointments shown
  const visible = useMemo(() => {
    const start = view === 'week' ? columns[0]?.key : anchorDate;
    const end = view === 'week' ? columns[columns.length - 1]?.key : anchorDate;
    return appointments.filter((a) => a.date >= start && a.date <= end);
  }, [appointments, columns, view, anchorDate]);

  const startMin = hourStart * 60;
  const endMin = hourEnd * 60;
  const rows = (endMin - startMin) / 30;

  const hourLabels = useMemo(() => {
    const arr = [];
    for (let m = startMin; m < endMin; m += 60) arr.push(fmtTime(m));
    return arr;
  }, [startMin, endMin]);

  const cardFor = (apt, columnHeightPx) => {
    const aStart = Math.max(toMinutes(apt.start_time), startMin);
    const aEnd = Math.min(toMinutes(apt.end_time), endMin);
    if (aEnd <= aStart) return null;
    const top = ((aStart - startMin) / 30) * slotPx;
    const height = Math.max(((aEnd - aStart) / 30) * slotPx - 2, 24);
    const style = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
    return (
      <button
        key={apt.id}
        type="button"
        onClick={() => onCardClick?.(apt)}
        className={`absolute left-1 right-1 ${style.bg} ${style.text} border-l-4 ${style.border} rounded-md px-1.5 py-1 text-[11px] text-left hover:shadow-md transition-shadow z-10 overflow-hidden`}
        style={{ top, height }}
        data-testid={`calendar-card-${apt.id}`}
      >
        <div className="font-semibold truncate">{apt.start_time} {apt.client_name}</div>
        <div className="truncate opacity-80">{apt.service}</div>
        {apt.staff_name && <div className="truncate opacity-70 italic">{apt.staff_name}</div>}
      </button>
    );
  };
  const columnHeightPx = rows * slotPx;

  return (
    <Card className="overflow-x-auto border border-[#E5E5E5]" data-testid="calendar-grid">
      <div
        className="grid bg-white"
        style={{
          gridTemplateColumns: `60px repeat(${columns.length}, minmax(120px, 1fr))`,
          minWidth: 60 + columns.length * 120,
        }}
      >
        {/* header row */}
        <div className="sticky top-0 bg-gray-50 border-b border-r border-[#E5E5E5] h-12" />
        {columns.map((col) => (
          <div
            key={col.key}
            className={`sticky top-0 bg-gray-50 border-b border-r border-[#E5E5E5] h-12 flex flex-col items-center justify-center text-center ${col.isToday ? 'bg-blue-50' : ''}`}
          >
            <div className="text-xs font-semibold text-[#1A1A2E]">{col.title}</div>
            <div className="text-[10px] text-[#999]">{col.subtitle}</div>
          </div>
        ))}

        {/* time labels column */}
        <div className="border-r border-[#E5E5E5] relative" style={{ height: columnHeightPx }}>
          {hourLabels.map((label, i) => (
            <div
              key={label}
              className="absolute left-0 right-0 px-1 text-[10px] text-[#999] text-right pr-2"
              style={{ top: i * 2 * slotPx - 6 }}
            >
              {label}
            </div>
          ))}
        </div>

        {columns.map((col) => {
          const colApts = visible.filter(col.match);
          return (
            <div
              key={col.key}
              className="relative border-r border-[#E5E5E5]"
              style={{ height: columnHeightPx }}
              data-testid={`calendar-column-${col.key}`}
            >
              {/* horizontal hour lines */}
              {Array.from({ length: rows }).map((_, i) => (
                <div
                  key={i}
                  className={`absolute left-0 right-0 ${i % 2 === 0 ? 'border-t border-[#F3F4F6]' : 'border-t border-dashed border-[#F9FAFB]'}`}
                  style={{ top: i * slotPx, height: slotPx }}
                />
              ))}
              {colApts.map((a) => cardFor(a, columnHeightPx))}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const StatusLegend = () => (
  <div className="flex flex-wrap gap-3 text-[11px] mt-3" data-testid="status-legend">
    {Object.entries({
      scheduled: 'Onaylandı',
      pending_approval: 'Onay Bekliyor',
      completed: 'Tamamlandı',
      no_show: 'Gelmedi',
      cancelled: 'İptal',
    }).map(([k, label]) => {
      const s = STATUS_STYLE[k];
      return (
        <div key={k} className="flex items-center gap-1.5">
          <span className={`w-3 h-3 rounded-sm ${s.bg} border-l-2 ${s.border}`} />
          <span className="text-[#595959]">{label}</span>
        </div>
      );
    })}
  </div>
);
